// src/pages/OrderDetails.jsx
import { useState, useEffect, useRef } from 'react';
import { Box, Grid, TextField, CircularProgress, Paper, Typography, InputAdornment, IconButton, Button, Snackbar, Divider, MenuItem, Badge, Unstable_TrapFocus as TrapFocus } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { MdShoppingCartCheckout } from 'react-icons/md';

const API_URL = `http://192.168.1.248:3103/api`;

export default function OrderDetails({ loggedInUser }) {
    const prevSearchRef = useRef('');
    const cardRefs = useRef({});

    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [expandedCardId, setExpandedCardId] = useState(null);
    const [quantities, setQuantities] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});
    const [selectedSizes, setSelectedSizes] = useState({});
    const [selectedColors, setSelectedColors] = useState({});
    const [bounce, setBounce] = useState(false);
    const [selectionErrors, setSelectionErrors] = useState({});
    const [imageMap, setImageMap] = useState({});
    const [carouselIndex, setCarouselIndex] = useState({});
    const [pauseCarousel, setPauseCarousel] = useState({});

    const { addItem, cartItems } = useCart();
    const navigate = useNavigate();

    // fetch color & size masters
    useEffect(() => {
        (async () => {
            try {
                const [cRes, sRes] = await Promise.all([
                    axios.get(`${API_URL}/colormaster`),
                    axios.get(`${API_URL}/sizemaster`)
                ]);
                const cm = {}, sm = {};
                cRes.data.forEach(c => cm[c.colorId] = { name: c.itemColor, code: c.colorCode });
                sRes.data.forEach(s => sm[s.sizeId] = { name: s.itemSize, code: s.sizeCode });
                setColorMap(cm);
                setSizeMap(sm);
            } catch { /* ignore */ }
        })();
    }, []);

    const colorOptions = Object.entries(colorMap).map(([id, v]) => ({ id: +id, ...v }));
    const sizeOptions = Object.entries(sizeMap).map(([id, v]) => ({ id: +id, ...v }));

    // cart bounce
    useEffect(() => {
        if (!Object.keys(cartItems).length) return setBounce(false);
        const iv = setInterval(() => setBounce(b => !b), 2000);
        return () => clearInterval(iv);
    }, [cartItems]);

    // fetch products
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/product/search?mode=search&itemCode=${searchTerm}`);
            const items = Array.isArray(res.data) ? res.data : [];
            setProducts(items);
            setQuantities(items.reduce((a, p) => { a[p.Id] = p.minOrderQty || 1; return a; }, {}));

            const imap = {};
            await Promise.all(items.map(async p => {
                try {
                    const ir = await axios.get(`${API_URL}/product/images/${p.Id}`);
                    imap[p.Id] = Array.isArray(ir.data) && ir.data.length
                        ? ir.data.sort((a, b) => a.sequence - b.sequence).map(i => ({ url: `data:${i.mimeType};base64,${i.image}`, sequence: i.sequence }))
                        : [];
                } catch {
                    imap[p.Id] = [];
                }
            }));
            setImageMap(imap);
        } catch {
            /*ignore*/
        }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchProducts(); }, []);

    // carousel rotate
    useEffect(() => {
        const iv = setInterval(() => {
            setCarouselIndex(prev => {
                const nxt = { ...prev };
                for (const pid in imageMap) {
                    const imgs = imageMap[pid];
                    if (imgs?.length > 1 && !pauseCarousel[pid]) {
                        nxt[pid] = ((prev[pid] || 0) + 1) % imgs.length;
                    }
                }
                return nxt;
            });
        }, 6000);
        return () => clearInterval(iv);
    }, [imageMap, pauseCarousel]);

    const handleSearch = e => e.key === 'Enter' && fetchProducts();
    const toggleExpand = id => setExpandedCardId(prev => prev === id ? null : id);
    const handleIncrement = id => setQuantities(q => ({ ...q, [id]: q[id] + 1 }));
    const handleDecrement = (id, min) => setQuantities(q => ({ ...q, [id]: Math.max(min, q[id] - 1) }));

    const handleAddToCart = p => {
        const pid = p.Id, qty = quantities[pid] || p.minOrderQty || 1;
        const sz = selectedSizes[pid], col = selectedColors[pid], uid = loggedInUser?.userId;
        if (!sz || !col) {
            setSelectionErrors(se => ({ ...se, [pid]: { size: !sz, color: !col } }));
            return;
        }
        setSelectionErrors(se => ({ ...se, [pid]: {} }));
        addItem({ ...p, sizeId: sz, colorId: col, userId: uid }, qty);
        setSnackbarOpen(true);
        setExpandedCardId(null);
    };

    // collapse on outside click
    useEffect(() => {
        const onOut = e => {
            if (!expandedCardId) return;
            const el = cardRefs.current[expandedCardId];
            if (el?.contains(e.target) || e.target.closest('.MuiPopover-root')) return;
            setExpandedCardId(null);
        };
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, [expandedCardId]);

    // keyboard nav + collapse logic
    const handleCardKey = (e, id, idx) => {
        const isExp = expandedCardId === id;
        // collapse on Escape
        if (e.key === 'Escape') {
            e.preventDefault();
            return setExpandedCardId(null);
        }
        // when expanded, up/down change qty
        if (isExp) {
            if (e.key === 'ArrowUp') { e.preventDefault(); handleIncrement(id); }
            if (e.key === 'ArrowDown') { e.preventDefault(); handleDecrement(id, products[idx].minOrderQty || 1); }
        }
        // left/right always collapse + move focus
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            setExpandedCardId(null);
            const delta = e.key === 'ArrowRight' ? 1 : -1;
            const ni = (idx + delta + products.length) % products.length;
            return cardRefs.current[products[ni].Id]?.focus();
        }
        // only expand/collapse on Enter/Space when not expanded
        if (!isExp && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toggleExpand(id);
        }
    };

    return (
        <Box p={2} maxWidth="1200px" mx="auto">
            {/* Search + Cart */}
            <Box
                position="fixed" top={72} left="50%" sx={{ transform: 'translateX(-50%)' }}
                width={{ xs: '92%', sm: '80%', md: '60%' }} zIndex={1200}
                bgcolor="background.paper" boxShadow={6} borderRadius={3}
                px={{ xs: 2, sm: 3 }} py={1.5}
                display="flex" gap={2}
            >
                <TextField
                    fullWidth placeholder="Search products..."
                    value={searchTerm}
                    onChange={e => { prevSearchRef.current = searchTerm; setSearchTerm(e.target.value); }}
                    onKeyDown={handleSearch}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        sx: { borderRadius: 9, bgcolor: 'background.default' }
                    }}
                    aria-label="Search products"
                />
                <Badge
                    badgeContent={Object.keys(cartItems).length}
                    color="error"
                    invisible={!Object.keys(cartItems).length}
                    overlap="circular"
                >
                    <IconButton
                        onClick={() => navigate('/main/cart-page')}
                        sx={{
                            bgcolor: 'grey.100', borderRadius: 3, p: 2,
                            transition: 'transform .2s',
                            animation: bounce ? 'bounce .5s ease' : 'none',
                            '@keyframes bounce': { '0%,100%': { transform: 'translateY(0)' }, '30%': { transform: 'translateY(-6px)' } },
                            '&:hover': { bgcolor: 'grey.200' }
                        }}
                        aria-label="View cart"
                    >
                        <MdShoppingCartCheckout size={26} />
                    </IconButton>
                </Badge>
            </Box>

            {/* Loading / Empty */}
            {loading
                ? <CircularProgress sx={{ mt: 12 }} />
                : !products.length
                    ? <Typography sx={{ mt: 12 }}>No products found.</Typography>
                    : (
                        <Grid container spacing={3} mt={8}>
                            {products.map((p, idx) => {
                                const isExp = expandedCardId === p.Id;
                                const minQty = p.minOrderQty || 1;
                                const qty = quantities[p.Id] || minQty;
                                return (
                                    <Grid item xs={6} sm={6} md={3} key={p.Id}>
                                        <Paper
                                            ref={el => cardRefs.current[p.Id] = el}
                                            tabIndex={0}
                                            onKeyDown={e => handleCardKey(e, p.Id, idx)}
                                            onClick={() => toggleExpand(p.Id)}
                                            elevation={isExp ? 6 : 2}
                                            sx={{
                                                borderRadius: 3, cursor: 'pointer', overflow: 'hidden',
                                                transition: 'all .3s', '&:hover': { boxShadow: 8 },
                                                display: 'flex', flexDirection: 'column'
                                            }}
                                            role="button"
                                            aria-expanded={isExp}
                                            aria-label={`Product ${p.itemCode}`}
                                        >

                                            {/* Carousel */}
                                            <Box
                                                sx={{ height: { xs: 180, sm: 220, md: 240 }, position: 'relative', overflow: 'hidden' }}
                                                onMouseEnter={() => setPauseCarousel(pc => ({ ...pc, [p.Id]: true }))}
                                                onMouseLeave={() => setPauseCarousel(pc => ({ ...pc, [p.Id]: false }))}
                                            >
                                                {imageMap[p.Id] && imageMap[p.Id].length > 0
                                                    ? imageMap[p.Id].map((img, i) => (
                                                        <Box key={i} component="img" src={img.url} alt=""
                                                            sx={{
                                                                position: 'absolute',
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'contain',
                                                                top: 0,
                                                                left: 0,
                                                                opacity: (carouselIndex[p.Id] || 0) === i ? 1 : 0,
                                                                transition: 'opacity .6s ease-in-out',
                                                            }}
                                                        />
                                                    ))
                                                    : (
                                                        // fallback to a random Lorem Picsum image
                                                        <Box component="img" src={`https://picsum.photos/seed/${p.Id}/400/300`} alt="Placeholder"
                                                            sx={{
                                                                position: 'absolute',
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                top: 0,
                                                                left: 0,
                                                            }}
                                                        />
                                                    )
                                                }

                                                {/* arrows */}
                                                {/* Prev / Next Arrows */}
                                                {(imageMap[p.Id] || []).length > 1 && (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setCarouselIndex(ci => ({
                                                                    ...ci,
                                                                    [p.Id]: ((ci[p.Id] || 0) - 1 + (imageMap[p.Id] || []).length) % (imageMap[p.Id] || []).length
                                                                }));
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: 4,
                                                                transform: 'translateY(-50%)',
                                                                bgcolor: 'rgba(255,255,255,0.7)',
                                                                '&:hover': { bgcolor: 'white' },
                                                                zIndex: 3
                                                            }}
                                                            aria-label="Previous image"
                                                        >
                                                            <ArrowBackIosNewIcon fontSize="inherit" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setCarouselIndex(ci => ({
                                                                    ...ci,
                                                                    [p.Id]: ((ci[p.Id] || 0) + 1) % (imageMap[p.Id] || []).length
                                                                }));
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                right: 4,
                                                                transform: 'translateY(-50%)',
                                                                bgcolor: 'rgba(255,255,255,0.7)',
                                                                '&:hover': { bgcolor: 'white' },
                                                                zIndex: 3
                                                            }}
                                                            aria-label="Next image"
                                                        >
                                                            <ArrowForwardIosIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </>
                                                )}

                                                {/* Dots Indicators */}
                                                {(imageMap[p.Id] || []).length > 1 && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 6,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            display: 'flex',
                                                            gap: 1,
                                                            zIndex: 4
                                                        }}
                                                    >
                                                        {(imageMap[p.Id] || []).map((_, i) => (
                                                            <Box
                                                                key={i}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    setCarouselIndex(ci => ({ ...ci, [p.Id]: i }));
                                                                }}
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: '50%',
                                                                    bgcolor: (carouselIndex[p.Id] || 0) === i ? 'primary.main' : 'rgba(255,255,255,0.7)',
                                                                    cursor: 'pointer',
                                                                    transition: 'background-color .3s',
                                                                }}
                                                                aria-label={`Go to image ${i + 1}`}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Details */}
                                            <Box px={2} py={2}>
                                                <Typography fontWeight={600} variant="subtitle1" gutterBottom noWrap>
                                                    {p.itemCode}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {p.description}
                                                </Typography>
                                                <Typography variant="h6" color="primary" mt={1}>
                                                    ₹{p.saleRate}
                                                </Typography>

                                                {isExp && (
                                                    <TrapFocus open disableAutoFocus>
                                                        <Box onClick={e => e.stopPropagation()}>
                                                            <Divider sx={{ my: 2 }} />
                                                            <Grid container spacing={2} mb={2}>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        select label="Color" size="small" fullWidth
                                                                        value={selectedColors[p.Id] || ''}
                                                                        error={selectionErrors[p.Id]?.color}
                                                                        helperText={selectionErrors[p.Id]?.color ? 'Select color' : ''}
                                                                        onChange={e => {
                                                                            setSelectedColors(sc => ({ ...sc, [p.Id]: +e.target.value }));
                                                                            setSelectionErrors(se => ({ ...se, [p.Id]: { ...se[p.Id], color: false } }));
                                                                        }}
                                                                        aria-label="Select color"
                                                                    >
                                                                        {colorOptions.map(opt => (
                                                                            <MenuItem key={opt.id} value={opt.id}>
                                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: opt.code, border: '1px solid #aaa' }} />
                                                                                    <Typography>{opt.name}</Typography>
                                                                                </Box>
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <TextField
                                                                        select label="Size" size="small" fullWidth
                                                                        value={selectedSizes[p.Id] || ''}
                                                                        error={selectionErrors[p.Id]?.size}
                                                                        helperText={selectionErrors[p.Id]?.size ? 'Select size' : ''}
                                                                        onChange={e => {
                                                                            setSelectedSizes(ss => ({ ...ss, [p.Id]: +e.target.value }));
                                                                            setSelectionErrors(se => ({ ...se, [p.Id]: { ...se[p.Id], size: false } }));
                                                                        }}
                                                                        aria-label="Select size"
                                                                    >
                                                                        {sizeOptions.map(opt => (
                                                                            <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                            </Grid>

                                                            <Typography variant="caption" color="text.secondary" display="block" align="center" mb={1}>
                                                                Minimum Order Qty: {minQty}
                                                            </Typography>

                                                            <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={2}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDecrement(p.Id, minQty)}
                                                                    disabled={quantities[p.Id] <= minQty}
                                                                    aria-label="Decrease quantity"
                                                                ><RemoveIcon fontSize="small" /></IconButton>
                                                                <Box position="relative">
                                                                    {/* sizing helper */}
                                                                    <Box component="span" sx={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', fontSize: '0.875rem', fontFamily: 'Roboto', px: 1 }}
                                                                        ref={el => {
                                                                            if (el) {
                                                                                const w = el.offsetWidth + 20;
                                                                                document.documentElement.style.setProperty(`--input-width-${p.Id}`, `${Math.max(40, w)}px`);
                                                                            }
                                                                        }}
                                                                    >{quantities[p.Id]}</Box>
                                                                    <TextField
                                                                        size="small" type="number" value={quantities[p.Id]}
                                                                        onClick={e => e.stopPropagation()}
                                                                        inputProps={{ min: minQty, 'aria-label': 'Quantity input' }}
                                                                        sx={{ width: `var(--input-width-${p.Id},40px)`, '& input': { textAlign: 'center', padding: '4px 8px' } }}
                                                                        onChange={e => {
                                                                            const v = parseInt(e.target.value, 10);
                                                                            if (!isNaN(v) && v >= minQty) {
                                                                                setQuantities(q => ({ ...q, [p.Id]: v }));
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleIncrement(p.Id)}
                                                                    aria-label="Increase quantity"
                                                                ><AddIcon fontSize="small" /></IconButton>
                                                            </Box>

                                                            <Button
                                                                variant="contained" color="primary" fullWidth
                                                                disabled={quantities[p.Id] < minQty}
                                                                onClick={() => handleAddToCart(p)}
                                                                sx={{ borderRadius: 2 }}
                                                                aria-label="Add to cart"
                                                            >
                                                                Add to Cart
                                                            </Button>
                                                        </Box>
                                                    </TrapFocus>
                                                )}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )
            }

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} message="Added to Cart" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}